<template>
  <component
    :is="dialog ? 'q-dialog' : 'div'"
    v-if="shown"
    v-model="shown"
    :maximized="dialog"
    data-test-id="category-dialog"
  >
    <q-card
      v-if="category"
      :class="{
        'bg-transparent no-shadow': !dialog,
        'rounded-borders-8': dialog && $q.screen.gt.xs
      }"
      :style="{
        width: $q.screen.lt.sm || !dialog ? '100%' : '80%',
        height: $q.screen.lt.sm || !dialog ? undefined : '80%'
      }"
    >
      <q-card-section
        class="row items-center q-py-none"
        :class="{
          'q-px-md': $q.screen.lt.sm || dialog,
          'q-px-none': !dialog && $q.screen.gt.xs,
          'q-pt-lg q-pb-sm': dialog
        }"
      >
        <div
          class="text-h6 text-center"
          style="line-height: 1.5;"
        >
          <slot name="title">
            {{ category.name }}
          </slot>
        </div>
        <q-space />
        <q-btn
          v-if="dialog"
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section
        :class="$q.screen.lt.sm || dialog ? 'q-px-md' : 'q-px-none'"
      >
        <slot name="items">
          <div
            class="row q-gutter-md justify-start scroll"
            :class="{
              'item-center self-center': $q.screen.lt.sm && dialog,
              'content-start': !($q.screen.lt.sm && dialog),
              'q-px-md': !$isLandscapeView() && !$q.screen.lt.sm
            }"
          >
            <item-card
              v-for="item in availableItems"
              :key="item.id"
              :item="item"
              :order="order"
              :display-price="displayPrice"
              :item-width="itemWidth"
              :dialog="dialog"
              :is-mobile="isMobile"
              :hide-image="hideImage"
              :bg-color="'primary'"
              :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
              :has-alcohol-acknowledgement="category.hasAlcoholAcknowledgement"
              @click="adminApproved => createOrderItem(item, adminApproved)"
              @request-authorization="callback => $emit('request-authorization', callback)"
            />
            <package-card
              v-for="itemPackage in availableItemPackages"
              :key="itemPackage.id"
              :item-package="itemPackage"
              :order="order"
              :display-price="displayPrice"
              :item-width="itemWidth"
              :dialog="dialog"
              :is-mobile="isMobile"
              :hide-image="hideImage"
              :bg-color="'primary'"
              :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
              :has-alcohol-acknowledgement="category.hasAlcoholAcknowledgement"
              @click="adminApproved => createOrderItemPackage(itemPackage, adminApproved)"
              @request-authorization="callback => $emit('request-authorization', callback)"
            />
          </div>
        </slot>
      </q-card-section>
    </q-card>
    <order-item-form
      ref="orderItemForm"
      :display-price="displayPrice"
      :available-online="availableOnline"
      :round="round"
      @add-model="orderItem => notifyAdded(orderItem.itemOrderItem.item)"
    />
  </component>
</template>

<script>
import { Notify } from 'quasar'
import Category from 'src/models/item/Category'
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import Order from 'src/models/order/Order'
import ItemCard from 'src/modules/checkout/components/ItemCard'
import PackageCard from 'src/modules/checkout/components/PackageCard'
import { useAvailableItemPackages } from 'src/modules/checkout/composables/sales-availability/item-packages/AvailableItemPackages'
import { useAvailableItems } from 'src/modules/checkout/composables/sales-availability/items/AvailableItems'
import { useSalesChannel } from 'src/modules/checkout/composables/sales-channels/SalesChannel'
import OrderItemForm from 'src/modules/register/components/order-item-form/OrderItemForm'

export default {
  name: 'CategoryDialog',
  components: { ItemCard, OrderItemForm, PackageCard },
  configuration: ['currentSite', 'currentCircuit'],
  props: {
    modelValue: {
      type: Boolean,
      required: false
    },
    category: {
      type: Category,
      required: true
    },
    order: {
      type: Order,
      required: true
    },
    dialog: {
      type: Boolean,
      required: false,
      default: true
    },
    hideImage: {
      type: Boolean,
      required: false,
      default: false
    },
    bgColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    textColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false
    },
    availableOnline: {
      type: Boolean,
      required: false,
      default: false
    },
    round: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:model-value', 'request-authorization', 'show', 'hide'],
  setup (props) {
    const { computeSalesChannel } = useSalesChannel()
    const { computeAvailableItems } = useAvailableItems()
    const { computeAvailableItemPackages } = useAvailableItemPackages()

    return {
      computeSalesChannel,
      computeAvailableItems,
      computeAvailableItemPackages
    }
  },
  data () {
    return {
      shown: false
    }
  },
  computed: {
    salesChannel () {
      return this.computeSalesChannel(SALES_CHANNEL_TYPES.MENU, this.order)
    },
    availableItems () {
      return this.computeAvailableItems(this.category?.items?.data, { salesChannel: this.salesChannel, order: this.order })
    },
    availableItemPackages () {
      return this.computeAvailableItemPackages(this.category?.itemPackages?.data, { salesChannel: this.salesChannel, order: this.order })
    },
    itemWidth () {
      if (this.$q.screen.lt.sm) {
        return '45%'
      } else if (this.$q.screen.lt.md) {
        return '22%'
      } else {
        return '18%'
      }
    },
    displayPrice () {
      if (this.$register) {
        return !this.currentCircuit.posOptions.hide_item_price
      }

      return true
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
      if (this.shown) {
        this.$emit('show')
        if (!_.isNil(this.category)) this.$trackPage(this, { category: this.category.name })
      } else {
        this.$emit('hide')
      }
    }
  },
  methods: {
    show () {
      this.shown = true
    },
    hide () {
      this.shown = false
    },
    createOrderItem (item, adminApproved = false) {
      let orderItem
      if (item.itemPreference) {
        orderItem = this.order.getFavoriteOrderItem(item.itemPreference)
      } else {
        orderItem = this.order.itemOrderItemForItem(item, { adminApproved })
      }

      // Skip showing detail on register since they can see the cart and can easily edit if needed
      if (item.skipDetailDialog) {
        this.order.orderItems.add(orderItem)
        orderItem.order = this.order
        this.notifyAdded(item)
      } else {
        orderItem.order = this.order
        this.$refs.orderItemForm.setCollection(this.order.orderItems)
        this.$refs.orderItemForm.show(orderItem)
      }
    },
    createOrderItemPackage (itemPackage, adminApproved = false) {
      const orderItems = this.order.addItemPackage(itemPackage, null, null, false)

      if (orderItems && orderItems.length > 0) {
        this.notifyAdded(itemPackage)
      }
    },
    notifyAdded (item) {
      if (this.dialog) {
        this.shown = false // Close the dialog after adding the item to the cart
      }
      if (this.$register) return
      Notify.create({
        message: `${item.name} added to Cart`,
        type: 'positive',
        position: 'bottom',
        group: false,
        timeout: 2500,
        classes: 'added-to-cart-notif'
      })
    }
  }
}
</script>

<style scoped lang="scss">
.added-to-cart-notif {
  margin-bottom: 60px;
  margin-top: -50px;
}

.q-dialog__inner--maximized > div {
  // I have no idea why we need to do this, but the dialog doesn't doesn't
  // center vertically if we don't.
  width: unset;
}

.rounded-borders-8 {
  border-radius: 8px !important;
}
</style>
